import { useEffect, useRef, useCallback } from 'react';
import { Map } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { BaseNode } from './useNearbyBaseNodes';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store/store';
import {
  selectBestHeight,
  getPathLoss,
  getMidpoint,
  calculateDistance,
  getLCCColor,
  formatDistance,
} from '../utils/sectorUtils';
import { MAP_LAYER_IDS } from '../constants/mapConstants';
import { hasActiveFrequency } from '../utils/nearbyBNsUtils';

interface UseCalculatePathlossSpeedVisualizationOptions {
  map: Map | null;
  activeBaseNode: BaseNode | null;
  onResultSelect?: (
    baseNode: BaseNode,
    remoteNode: any,
    remoteNodeList: any[]
  ) => void;
}

const POLYLINE_SOURCE_ID = 'single-sector-polyline';
const POLYLINE_LAYER_ID = 'single-sector-polyline';
const POLYLINE_BORDER_LAYER_ID = 'single-sector-polyline-border';

const MULTI_SECTOR_LINES_SOURCE_ID = 'multi-sector-lines';
const MULTI_SECTOR_LINES_LAYER_ID = 'multi-sector-lines';
const MULTI_SECTOR_LINES_BORDER_LAYER_ID = 'multi-sector-lines-border';

/**
 * Hook to visualize single and multi-sector prediction results
 * - Single sector: Draws a single polyline from base node to remote node with a label marker
 * - Multi-sector: Draws lines from remote node to each base node (arcs and nodes are handled by useNearbyBaseNodes)
 */
export const useCalculatePathlossSpeedVisualization = ({
  map,
  activeBaseNode,
  onResultSelect,
}: UseCalculatePathlossSpeedVisualizationOptions) => {
  const sectorResults = useAppSelector(
    (state: RootState) => state.servicePredictor.sectorResults
  );
  const activeBands = useAppSelector(
    (state: RootState) => state.servicePredictor.filters.activeBands
  );
  const remoteNode = useAppSelector(
    (state: RootState) => state.servicePredictor.remoteNode
  );
  const labelMarkerRef = useRef<mapboxgl.Marker[]>([]);
  const clickHandlerRef = useRef<
    ((e: mapboxgl.MapLayerMouseEvent) => void) | null
  >(null);

  const cleanupMarkers = useCallback(() => {
    labelMarkerRef.current.forEach((marker) => marker.remove());
    labelMarkerRef.current = [];
  }, []);

  const cleanupSingleSector = useCallback(
    (map: Map) => {
      if (clickHandlerRef.current) {
        map.off('click', POLYLINE_LAYER_ID, clickHandlerRef.current);
        clickHandlerRef.current = null;
      }

      cleanupMarkers();

      if (map.getLayer(POLYLINE_LAYER_ID)) {
        map.removeLayer(POLYLINE_LAYER_ID);
      }
      if (map.getLayer(POLYLINE_BORDER_LAYER_ID)) {
        map.removeLayer(POLYLINE_BORDER_LAYER_ID);
      }
      if (map.getSource(POLYLINE_SOURCE_ID)) {
        map.removeSource(POLYLINE_SOURCE_ID);
      }
    },
    [cleanupMarkers]
  );

  const cleanupMultiSector = useCallback(
    (map: Map) => {
      cleanupMarkers();

      if (clickHandlerRef.current) {
        map.off('click', MULTI_SECTOR_LINES_LAYER_ID, clickHandlerRef.current);
        clickHandlerRef.current = null;
      }

      if (map.getLayer(MULTI_SECTOR_LINES_LAYER_ID)) {
        map.removeLayer(MULTI_SECTOR_LINES_LAYER_ID);
      }
      if (map.getLayer(MULTI_SECTOR_LINES_BORDER_LAYER_ID)) {
        map.removeLayer(MULTI_SECTOR_LINES_BORDER_LAYER_ID);
      }
      if (map.getSource(MULTI_SECTOR_LINES_SOURCE_ID)) {
        map.removeSource(MULTI_SECTOR_LINES_SOURCE_ID);
      }
    },
    [cleanupMarkers]
  );

  /**
   * Determines the layer ID to place polylines before (so they appear behind site icons)
   * @param map - Mapbox map instance
   * @returns Layer ID to use as beforeId, or undefined if no site layers exist
   */
  const getBeforeSiteLayerId = (map: Map): string | undefined => {
    if (map.getLayer(MAP_LAYER_IDS.SITES_CLUSTERS)) {
      return MAP_LAYER_IDS.SITES_CLUSTERS;
    }
    if (map.getLayer(MAP_LAYER_IDS.SITES_UNCLUSTERED)) {
      return MAP_LAYER_IDS.SITES_UNCLUSTERED;
    }
    if (map.getLayer(MAP_LAYER_IDS.SITES_BN_COUNT)) {
      return MAP_LAYER_IDS.SITES_BN_COUNT;
    }
    if (map.getLayer('heatmap-overlay')) {
      return 'heatmap-overlay';
    }
    return undefined;
  };

  /**
   * Creates a GeoJSON line feature connecting a base node to a remote node
   * @param baseNode - Base node information
   * @param baseLat - Base node latitude
   * @param baseLng - Base node longitude
   * @param remoteLat - Remote node latitude
   * @param remoteLng - Remote node longitude
   * @param sectorIndex - Index of the sector in the list
   * @param LCC - Link Close Confidence value
   * @param strokeColor - Color for the line stroke
   * @param distanceText - Formatted distance string
   * @param pathLoss - Formatted path loss string
   * @param borderColor - Border color for the line
   * @param isSelected - Whether this base node is currently selected
   * @param bestHeightData - Best height data containing remote node information
   * @param remoteNodeList - List of remote nodes for this sector
   * @returns GeoJSON Feature representing the line
   */
  const createLineFeature = (
    baseNode: BaseNode,
    baseLat: number,
    baseLng: number,
    remoteLat: number,
    remoteLng: number,
    sectorIndex: number,
    LCC: number,
    strokeColor: string,
    distanceText: string,
    pathLoss: string,
    borderColor: string,
    isSelected: boolean,
    bestHeightData: { remote: any },
    remoteNodeList: any[]
  ): GeoJSON.Feature => {
    return {
      type: 'Feature',
      id: `multi-sector-line-${sectorIndex}`,
      properties: {
        baseNodeSerial: baseNode.serial,
        color: strokeColor,
        borderColor,
        lcc: LCC,
        distance: distanceText,
        pathLoss,
        sectorIndex,
        isSelected,
        baseNode,
        remoteNode: bestHeightData.remote,
        remoteNodeList,
      },
      geometry: {
        type: 'LineString',
        coordinates: [
          [baseLng, baseLat],
          [remoteLng, remoteLat],
        ],
      },
    };
  };

  /**
   * Creates a label marker at the midpoint between a base node and remote node
   * @param baseLat - Base node latitude
   * @param baseLng - Base node longitude
   * @param remoteLat - Remote node latitude
   * @param remoteLng - Remote node longitude
   * @param distanceText - Formatted distance string
   * @param LCC - Link Close Confidence value
   * @param pathLoss - Formatted path loss string
   * @param borderColor - Background color for the label
   * @param baseNode - Base node information
   * @param remoteNode - Remote node information
   * @param remoteNodeList - List of remote nodes for this sector
   * @param onResultSelect - Callback function when label is clicked
   * @param map - Mapbox map instance to add marker to
   * @returns Mapbox marker instance
   */
  const createLabelMarker = (
    baseLat: number,
    baseLng: number,
    remoteLat: number,
    remoteLng: number,
    distanceText: string,
    LCC: number,
    pathLoss: string,
    borderColor: string,
    baseNode: BaseNode,
    remoteNode: any,
    remoteNodeList: any[],
    onResultSelect:
      | ((baseNode: BaseNode, remoteNode: any, remoteNodeList: any[]) => void)
      | undefined,
    map: Map
  ): mapboxgl.Marker => {
    const midpoint = getMidpoint(
      { lat: baseLat, lng: baseLng },
      { lat: remoteLat, lng: remoteLng }
    );

    const labelText = `${distanceText} | LCC: ${(LCC * 100).toFixed(0)}%${pathLoss ? ` | PL: ${pathLoss}` : ''}`;

    const labelEl = document.createElement('div');
    labelEl.className = 'multi-sector-label-marker';
    labelEl.innerHTML = labelText;
    labelEl.style.backgroundColor = borderColor;
    labelEl.style.color = '#ffffff';
    labelEl.style.padding = '4px 8px';
    labelEl.style.borderRadius = '4px';
    labelEl.style.fontSize = '12px';
    labelEl.style.fontWeight = 'bold';
    labelEl.style.whiteSpace = 'nowrap';
    labelEl.style.cursor = 'pointer';
    labelEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    labelEl.style.zIndex = '1000';

    labelEl.addEventListener('click', () => {
      if (onResultSelect) {
        onResultSelect(baseNode, remoteNode, remoteNodeList);
      }
    });

    const marker = new mapboxgl.Marker({
      element: labelEl,
      anchor: 'center',
    })
      .setLngLat([midpoint.lng, midpoint.lat])
      .addTo(map);

    return marker;
  };

  const addPolylineSource = useCallback(
    (map: Map, feature: GeoJSON.Feature) => {
      if (!map.getSource(POLYLINE_SOURCE_ID)) {
        map.addSource(POLYLINE_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [feature],
          },
        });
      } else {
        const source = map.getSource(
          POLYLINE_SOURCE_ID
        ) as mapboxgl.GeoJSONSource;
        source.setData({
          type: 'FeatureCollection',
          features: [feature],
        });
      }
    },
    []
  );

  const addMultiSectorLinesSource = useCallback(
    (map: Map, lineFeatures: GeoJSON.Feature[]) => {
      if (!map.getSource(MULTI_SECTOR_LINES_SOURCE_ID)) {
        map.addSource(MULTI_SECTOR_LINES_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: lineFeatures,
          },
        });
      } else {
        const source = map.getSource(
          MULTI_SECTOR_LINES_SOURCE_ID
        ) as mapboxgl.GeoJSONSource;
        source.setData({
          type: 'FeatureCollection',
          features: lineFeatures,
        });
      }
    },
    []
  );

  const addPolylineBorderLayer = useCallback((map: Map) => {
    if (!map.getLayer(POLYLINE_BORDER_LAYER_ID)) {
      const beforeId = getBeforeSiteLayerId(map);
      map.addLayer(
        {
          id: POLYLINE_BORDER_LAYER_ID,
          type: 'line',
          source: POLYLINE_SOURCE_ID,
          paint: {
            'line-color': ['get', 'borderColor'],
            'line-width': 10,
            'line-opacity': 1.0,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        },
        beforeId
      );
    } else {
      map.setPaintProperty(POLYLINE_BORDER_LAYER_ID, 'line-color', [
        'get',
        'borderColor',
      ]);
    }
  }, []);

  const addPolylineLayer = useCallback((map: Map) => {
    if (!map.getLayer(POLYLINE_LAYER_ID)) {
      map.addLayer(
        {
          id: POLYLINE_LAYER_ID,
          type: 'line',
          source: POLYLINE_SOURCE_ID,
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 5,
            'line-opacity': 1.0,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        },
        POLYLINE_BORDER_LAYER_ID
      );
    }
  }, []);

  const addMultiSectorLinesBorderLayer = useCallback((map: Map) => {
    if (!map.getLayer(MULTI_SECTOR_LINES_BORDER_LAYER_ID)) {
      const beforeId = getBeforeSiteLayerId(map);
      map.addLayer(
        {
          id: MULTI_SECTOR_LINES_BORDER_LAYER_ID,
          type: 'line',
          source: MULTI_SECTOR_LINES_SOURCE_ID,
          paint: {
            'line-color': ['get', 'borderColor'],
            'line-width': 10,
            'line-opacity': 1.0,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          filter: ['==', ['get', 'isSelected'], true],
        },
        beforeId
      );
    } else {
      // Update the filter to show only selected lines
      map.setFilter(MULTI_SECTOR_LINES_BORDER_LAYER_ID, [
        '==',
        ['get', 'isSelected'],
        true,
      ]);
      // Update paint properties in case borderColor changed
      map.setPaintProperty(MULTI_SECTOR_LINES_BORDER_LAYER_ID, 'line-color', [
        'get',
        'borderColor',
      ]);
    }
  }, []);

  const addMultiSectorLinesLayer = useCallback((map: Map) => {
    if (!map.getLayer(MULTI_SECTOR_LINES_LAYER_ID)) {
      map.addLayer(
        {
          id: MULTI_SECTOR_LINES_LAYER_ID,
          type: 'line',
          source: MULTI_SECTOR_LINES_SOURCE_ID,
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 5,
            'line-opacity': 1.0,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        },
        MULTI_SECTOR_LINES_BORDER_LAYER_ID
      );
    }
  }, []);

  const addPolylineClickInteraction = useCallback(
    (
      map: Map,
      baseNodeData: any,
      selectedRemoteNode: any,
      remoteNodeList: any[]
    ) => {
      if (clickHandlerRef.current) {
        map.off('click', POLYLINE_LAYER_ID, clickHandlerRef.current);
      }

      const handlePolylineClick = (e: mapboxgl.MapLayerMouseEvent) => {
        if (e.features && e.features.length > 0 && onResultSelect) {
          const baseNode: BaseNode = {
            serial: baseNodeData.serial || '',
            cell: baseNodeData.cell || '',
            sector: baseNodeData.sector || '',
            hostname: baseNodeData.hostname || '',
            latitude: baseNodeData.latitude,
            longitude: baseNodeData.longitude,
            azimuth: baseNodeData.azimuth ?? null,
            downtilt: baseNodeData.downtilt ?? null,
            height_agl: baseNodeData.height_agl || 0,
            height_gps: baseNodeData.height_gps || 0,
            market: baseNodeData.market || '',
            site: baseNodeData.site || '',
            region: baseNodeData.region || '',
            carrier: baseNodeData.carrier || [],
            subscribers: baseNodeData.subscribers || 0,
            planning_id: baseNodeData.planning_id || '',
            part_number: baseNodeData.part_number || '',
            heatmap_list: baseNodeData.heatmap_list || [],
          };

          onResultSelect(baseNode, selectedRemoteNode, remoteNodeList);
        }
      };

      map.on('click', POLYLINE_LAYER_ID, handlePolylineClick);
      clickHandlerRef.current = handlePolylineClick;
    },
    [onResultSelect]
  );

  const addMultiSectorLinesClickInteraction = useCallback(
    (map: Map) => {
      if (clickHandlerRef.current) {
        map.off('click', MULTI_SECTOR_LINES_LAYER_ID, clickHandlerRef.current);
      }

      const handleLineClick = (e: mapboxgl.MapLayerMouseEvent) => {
        if (e.features && e.features.length > 0 && onResultSelect) {
          const feature = e.features[0];
          const properties = feature.properties as any;
          if (
            properties.baseNode &&
            properties.remoteNode &&
            properties.remoteNodeList
          ) {
            onResultSelect(
              properties.baseNode,
              properties.remoteNode,
              properties.remoteNodeList
            );
          }
        }
      };

      map.on('click', MULTI_SECTOR_LINES_LAYER_ID, handleLineClick);
      clickHandlerRef.current = handleLineClick;
    },
    [onResultSelect]
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    cleanupSingleSector(map);
    cleanupMultiSector(map);

    if (!sectorResults) {
      return;
    }

    if (sectorResults.sector_list && Array.isArray(sectorResults.sector_list)) {
      const sectorList = sectorResults.sector_list;
      if (sectorList.length === 0) {
        return;
      }

      const lineFeatures: GeoJSON.Feature[] = [];

      sectorList.forEach((sector: any, sectorIndex: number) => {
        const bnData = sector.bn;
        if (!bnData || !bnData.latitude || !bnData.longitude) {
          return;
        }

        if (!hasActiveFrequency(bnData, activeBands)) {
          return;
        }

        const baseNode: BaseNode = {
          serial: bnData.serial || '',
          cell: bnData.cell || '',
          sector: bnData.sector || '',
          hostname: bnData.hostname || '',
          latitude: parseFloat(bnData.latitude),
          longitude: parseFloat(bnData.longitude),
          azimuth: bnData.azimuth ?? null,
          downtilt: bnData.downtilt ?? null,
          height_agl: bnData.height_agl || 0,
          height_gps: bnData.height_gps || 0,
          market: bnData.market || '',
          site: bnData.site || '',
          region: bnData.region || '',
          carrier: bnData.carrier || [],
          subscribers: bnData.subscribers || 0,
          planning_id: bnData.planning_id || '',
          part_number: bnData.part_number || '',
          heatmap_list: bnData.heatmap_list || [],
        };

        const remoteLat = remoteNode.location?.lat;
        const remoteLng = remoteNode.location?.lng;

        if (
          remoteLat &&
          remoteLng &&
          sector.rn_list &&
          Array.isArray(sector.rn_list) &&
          sector.rn_list.length > 0
        ) {
          const bestHeightData = selectBestHeight(sector.rn_list);
          if (bestHeightData) {
            const LCC = bestHeightData.LCC;
            const strokeColor = getLCCColor(LCC);

            if (strokeColor) {
              const baseLat = baseNode.latitude;
              const baseLng = baseNode.longitude;

              const distance = calculateDistance(
                { lat: baseLat, lng: baseLng },
                { lat: remoteLat, lng: remoteLng }
              );
              const distanceText = formatDistance(distance);
              const pathLoss = getPathLoss(bestHeightData.remote);

              const isSelected = activeBaseNode?.serial === baseNode.serial;
              const borderColor = isSelected ? '#0084b4' : '#15202b';

              const lineFeature = createLineFeature(
                baseNode,
                baseLat,
                baseLng,
                remoteLat,
                remoteLng,
                sectorIndex,
                LCC,
                strokeColor,
                distanceText,
                pathLoss,
                borderColor,
                isSelected,
                bestHeightData,
                sector.rn_list
              );

              lineFeatures.push(lineFeature);

              const marker = createLabelMarker(
                baseLat,
                baseLng,
                remoteLat,
                remoteLng,
                distanceText,
                LCC,
                pathLoss,
                borderColor,
                baseNode,
                bestHeightData.remote,
                sector.rn_list,
                onResultSelect,
                map
              );

              labelMarkerRef.current.push(marker);
            }
          }
        }
      });

      if (lineFeatures.length > 0) {
        addMultiSectorLinesSource(map, lineFeatures);
        addMultiSectorLinesBorderLayer(map);
        addMultiSectorLinesLayer(map);
        addMultiSectorLinesClickInteraction(map);
      }

      return () => {
        cleanupMultiSector(map);
      };
    }

    const baseNodeData = sectorResults.bn;
    const remoteNodeList = sectorResults.rn_list;

    if (
      !baseNodeData ||
      !remoteNodeList ||
      !Array.isArray(remoteNodeList) ||
      remoteNodeList.length === 0
    ) {
      return;
    }

    if (!hasActiveFrequency(baseNodeData, activeBands)) {
      return;
    }

    const bestHeightData = selectBestHeight(remoteNodeList);
    if (!bestHeightData) {
      return;
    }

    const selectedRemoteNode = bestHeightData.remote;
    const LCC = bestHeightData.LCC;

    const strokeColor = getLCCColor(LCC);
    if (!strokeColor) {
      return;
    }

    const baseLat = baseNodeData.latitude;
    const baseLng = baseNodeData.longitude;
    const remoteLat = selectedRemoteNode.rn.latitude;
    const remoteLng = selectedRemoteNode.rn.longitude;

    if (
      isNaN(baseLat) ||
      isNaN(baseLng) ||
      isNaN(remoteLat) ||
      isNaN(remoteLng)
    ) {
      return;
    }

    const distance = calculateDistance(
      { lat: baseLat, lng: baseLng },
      { lat: remoteLat, lng: remoteLng }
    );
    const distanceText = formatDistance(distance);

    const pathLoss = getPathLoss(selectedRemoteNode);

    const isSelected =
      !activeBaseNode || activeBaseNode.serial === baseNodeData.serial;
    const borderColor = isSelected ? '#0084b4' : '#15202b';

    const feature: GeoJSON.Feature = {
      type: 'Feature',
      id: 'single-sector-line',
      properties: {
        baseNodeSerial: baseNodeData.serial,
        color: strokeColor,
        borderColor,
        lcc: LCC,
        distance: distanceText,
        pathLoss,
        isSelected,
      },
      geometry: {
        type: 'LineString',
        coordinates: [
          [baseLng, baseLat],
          [remoteLng, remoteLat],
        ],
      },
    };

    addPolylineSource(map, feature);
    addPolylineBorderLayer(map);
    addPolylineLayer(map);
    addPolylineClickInteraction(
      map,
      baseNodeData,
      selectedRemoteNode,
      remoteNodeList
    );

    const midpoint = getMidpoint(
      { lat: baseLat, lng: baseLng },
      { lat: remoteLat, lng: remoteLng }
    );

    const labelText = `${distanceText} | LCC: ${(LCC * 100).toFixed(0)}%${pathLoss ? ` | PL: ${pathLoss}` : ''}`;

    const labelEl = document.createElement('div');
    labelEl.className = 'single-sector-label-marker';
    labelEl.innerHTML = labelText;
    labelEl.style.backgroundColor = borderColor;
    labelEl.style.color = '#ffffff';
    labelEl.style.padding = '4px 8px';
    labelEl.style.borderRadius = '4px';
    labelEl.style.fontSize = '12px';
    labelEl.style.fontWeight = 'bold';
    labelEl.style.whiteSpace = 'nowrap';
    labelEl.style.cursor = 'pointer';
    labelEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    labelEl.style.zIndex = '1000';

    labelEl.addEventListener('click', () => {
      if (onResultSelect) {
        const baseNode: BaseNode = {
          serial: baseNodeData.serial || '',
          cell: baseNodeData.cell || '',
          sector: baseNodeData.sector || '',
          hostname: baseNodeData.hostname || '',
          latitude: baseNodeData.latitude,
          longitude: baseNodeData.longitude,
          azimuth: baseNodeData.azimuth,
          downtilt: baseNodeData.downtilt,
          height_agl: baseNodeData.height_agl || 0,
          height_gps: baseNodeData.height_gps || 0,
          market: baseNodeData.market || '',
          site: baseNodeData.site || '',
          region: baseNodeData.region || '',
          carrier: baseNodeData.carrier || [],
          subscribers: baseNodeData.subscribers || 0,
          planning_id: baseNodeData.planning_id || '',
          part_number: baseNodeData.part_number || '',
          heatmap_list: baseNodeData.heatmap_list || [],
        };

        onResultSelect(baseNode, selectedRemoteNode, remoteNodeList);
      }
    });

    const marker = new mapboxgl.Marker({
      element: labelEl,
      anchor: 'center',
    })
      .setLngLat([midpoint.lng, midpoint.lat])
      .addTo(map);

    labelMarkerRef.current = [marker];

    return () => {
      cleanupSingleSector(map);
    };
  }, [
    map,
    sectorResults,
    activeBands,
    activeBaseNode,
    remoteNode,
    onResultSelect,
    cleanupSingleSector,
    cleanupMultiSector,
    addPolylineSource,
    addPolylineBorderLayer,
    addPolylineLayer,
    addPolylineClickInteraction,
    addMultiSectorLinesSource,
    addMultiSectorLinesBorderLayer,
    addMultiSectorLinesLayer,
    addMultiSectorLinesClickInteraction,
  ]);

  useEffect(() => {
    return () => {
      if (map) {
        cleanupSingleSector(map);
        cleanupMultiSector(map);
      }
    };
  }, [map, cleanupSingleSector, cleanupMultiSector]);
};
